/* =========================================================================
   Admin Development-Plan editor — redux actions (appManager.createApi).

   Consumes pm/devplan/admin_views.py. Lifecycle (create/clone/publish/hide/
   delete) lives on DevPlanAdminViewSet; per-entity writes hit the draft-scoped
   ViewSets and carry the plan `code` in the body (the app's ApiCaller only
   appends the query string on GET). Field edits are debounced-PATCHed by the
   inspectors (optimistic autosave); `saving` counts in-flight writes for the
   "Saving…" chip. successReducers keep `content` (the open graph) in sync so
   the canvas re-renders without a refetch.
   ========================================================================= */

import { appManager, AppState } from '../manager';
// Re-export the slice DTOs so `import { DevPlanContent, ... } from 'state'` works
// (the state barrel picks up this actions file).
export * from './types';
import type {
  DevAssetAdmin,
  DevClusterAdmin,
  DevEdgeAdmin,
  DevEdgeKind,
  DevGameRole,
  DevNodeAdmin,
  DevNodeGameAdmin,
  DevPlanAdmin,
  DevPlanContent,
  DevRecommendationAdmin,
  DevRecommendationTarget,
  DevTrackAdmin,
  RemediationRouteAdmin,
} from './types';

/* ---- draft-content mutation helpers (operate on the immer draft) -------- */

function replaceById<T extends { id: number }>(arr: T[], item: T): void {
  const i = arr.findIndex((x) => x.id === item.id);
  if (i >= 0) arr[i] = item;
  else arr.push(item);
}
// Strict replace: for PATCH responses that may land after a plan switch — never
// inject a foreign row into the wrong plan's content (no push on miss).
function replaceExisting<T extends { id: number }>(arr: T[], item: T): void {
  const i = arr.findIndex((x) => x.id === item.id);
  if (i >= 0) arr[i] = item;
}
function removeById<T extends { id: number }>(arr: T[], id: number): void {
  const i = arr.findIndex((x) => x.id === id);
  if (i >= 0) arr.splice(i, 1);
}
function upsertPlan(state: AppState, plan: DevPlanAdmin): void {
  replaceById(state.devplanAdmin.plans, plan);
  const c = state.devplanAdmin.content;
  if (c && c.plan.id === plan.id) c.plan = plan;
}
function startSaving(state: AppState): void {
  state.devplanAdmin.saving += 1;
  state.devplanAdmin.error = undefined;
}
function endSaving(state: AppState): void {
  state.devplanAdmin.saving = Math.max(0, state.devplanAdmin.saving - 1);
}
function writeFailed(state: AppState, error: unknown): void {
  endSaving(state);
  state.devplanAdmin.error = stringifyError(error);
}
function stringifyError(error: unknown): string {
  if (typeof error === 'string') return error;
  const anyErr = error as any;
  if (anyErr?.detail) return String(anyErr.detail);
  try {
    return JSON.stringify(error);
  } catch {
    return 'Request failed.';
  }
}
function extractProblems(error: unknown): string[] {
  const anyErr = error as any;
  const raw = Array.isArray(anyErr)
    ? anyErr
    : anyErr?.detail ?? anyErr?.problems ?? anyErr;
  if (Array.isArray(raw)) return raw.map((p) => String(p));
  if (typeof raw === 'string') return [raw];
  if (raw && typeof raw === 'object') {
    return Object.values(raw).flat().map((p) => String(p));
  }
  return ['Publish blocked (see server).'];
}

/* ======================= reads ========================================== */

// GET /devplan/admin/plans — every tree (all statuses) for the rail.
export const fetchAdminPlans = appManager.createApi<
  Record<string, never>,
  DevPlanAdmin[],
  AppState
>('DEVADMIN_FETCH_PLANS', {
  method: 'GET',
  path: '/devplan/admin/plans',
  successReducer(state, result) {
    state.devplanAdmin.plans = Array.isArray(result) ? result : [];
    state.devplanAdmin.plansFetched = true;
  },
  failReducer(state, error) {
    state.devplanAdmin.error = stringifyError(error);
  },
});

// GET /devplan/admin/assets — the global HTML pool (scenario + game pickers).
export const fetchAdminAssets = appManager.createApi<
  Record<string, never>,
  DevAssetAdmin[],
  AppState
>('DEVADMIN_FETCH_ASSETS', {
  method: 'GET',
  path: '/devplan/admin/assets',
  successReducer(state, result) {
    state.devplanAdmin.assets = Array.isArray(result) ? result : [];
    state.devplanAdmin.assetsFetched = true;
  },
});

// GET /devplan/admin/plans/<code>/content — the full editable graph (incl. inactive
// nodes + internal_code) for the open plan.
export const fetchPlanContent = appManager.createApi<
  { code: string },
  DevPlanContent,
  AppState
>('DEVADMIN_FETCH_CONTENT', {
  method: 'GET',
  path: '/devplan/admin/plans/:code/content',
  successReducer(state, result) {
    state.devplanAdmin.content = result;
    state.devplanAdmin.contentCode = result.plan.code;
    state.devplanAdmin.contentFetched = true;
    state.devplanAdmin.publishProblems = undefined;
  },
  failReducer(state, error) {
    state.devplanAdmin.contentFetched = false;
    state.devplanAdmin.error = stringifyError(error);
  },
});

/* ======================= plan lifecycle ================================= */

export const createPlan = appManager.createApi<
  { code: string; name: string; description: string },
  DevPlanAdmin,
  AppState
>('DEVADMIN_CREATE_PLAN', {
  method: 'POST',
  path: '/devplan/admin/plans',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    replaceById(state.devplanAdmin.plans, result);
  },
  failReducer: writeFailed,
});

export const clonePlan = appManager.createApi<
  { code: string; new_code: string; new_name: string },
  DevPlanAdmin,
  AppState
>('DEVADMIN_CLONE_PLAN', {
  method: 'POST',
  path: '/devplan/admin/plans/:code/clone',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    replaceById(state.devplanAdmin.plans, result);
  },
  failReducer: writeFailed,
});

export const publishPlan = appManager.createApi<
  { code: string },
  DevPlanAdmin,
  AppState
>('DEVADMIN_PUBLISH_PLAN', {
  method: 'POST',
  path: '/devplan/admin/plans/:code/publish',
  startReducer(state) {
    startSaving(state);
    state.devplanAdmin.publishProblems = undefined;
  },
  successReducer(state, result) {
    endSaving(state);
    upsertPlan(state, result);
    state.devplanAdmin.publishProblems = undefined;
  },
  // 409 → the N5 completeness problem list (not a generic error).
  failReducer(state, error) {
    endSaving(state);
    state.devplanAdmin.publishProblems = extractProblems(error);
  },
});

export const hidePlan = appManager.createApi<
  { code: string },
  DevPlanAdmin,
  AppState
>('DEVADMIN_HIDE_PLAN', {
  method: 'POST',
  path: '/devplan/admin/plans/:code/hide',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    upsertPlan(state, result);
  },
  failReducer: writeFailed,
});

export const unhidePlan = appManager.createApi<
  { code: string },
  DevPlanAdmin,
  AppState
>('DEVADMIN_UNHIDE_PLAN', {
  method: 'POST',
  path: '/devplan/admin/plans/:code/unhide',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    upsertPlan(state, result);
  },
  failReducer: writeFailed,
});

export const deletePlan = appManager.createApi<
  { code: string },
  null,
  AppState
>('DEVADMIN_DELETE_PLAN', {
  method: 'DELETE',
  path: '/devplan/admin/plans/:code',
  startReducer: startSaving,
  successReducer(state, _result, params) {
    endSaving(state);
    const i = state.devplanAdmin.plans.findIndex((p) => p.code === params.code);
    if (i >= 0) state.devplanAdmin.plans.splice(i, 1);
    if (state.devplanAdmin.contentCode === params.code) {
      state.devplanAdmin.content = undefined;
      state.devplanAdmin.contentCode = undefined;
      state.devplanAdmin.contentFetched = false;
    }
  },
  failReducer: writeFailed,
});

export const patchPlanMeta = appManager.createApi<
  { code: string; name?: string; description?: string; order?: number },
  DevPlanAdmin,
  AppState
>('DEVADMIN_PATCH_PLAN', {
  method: 'PATCH',
  path: '/devplan/admin/plans/:code',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    upsertPlan(state, result);
  },
  failReducer: writeFailed,
});

/* ======================= node ========================================== */

export interface PatchNodePayload {
  id: number;
  plan: string;
  name?: string;
  description?: string;
  code?: string;
  tier?: string;
  required_level?: number;
  is_active?: boolean;
  track?: number;
  group?: string;
}

export const patchNode = appManager.createApi<
  PatchNodePayload,
  DevNodeAdmin,
  AppState
>('DEVADMIN_PATCH_NODE', {
  method: 'PATCH',
  path: '/devplan/admin/nodes/:id',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      replaceExisting(state.devplanAdmin.content.nodes, result);
    }
  },
  failReducer: writeFailed,
});

export const deleteNode = appManager.createApi<
  { id: number; plan: string },
  null,
  AppState
>('DEVADMIN_DELETE_NODE', {
  method: 'DELETE',
  path: '/devplan/admin/nodes/:id',
  startReducer: startSaving,
  // The server cascades edges/games/remediation off the node; prune them locally
  // too so the canvas + inspectors don't render dangling references.
  successReducer(state, _result, params) {
    endSaving(state);
    const c = state.devplanAdmin.content;
    if (!c) return;
    removeById(c.nodes, params.id);
    c.edges = c.edges.filter(
      (e) => e.from_node !== params.id && e.to_node !== params.id
    );
    c.node_games = c.node_games.filter((g) => g.node !== params.id);
    c.remediation = c.remediation.filter(
      (r) => r.node !== params.id && r.target_node !== params.id
    );
  },
  failReducer: writeFailed,
});

/* ======================= edges ========================================= */

export const createEdge = appManager.createApi<
  {
    plan: string;
    from_node: number;
    to_node: number;
    kind: DevEdgeKind;
    required_rank: number;
  },
  DevEdgeAdmin,
  AppState
>('DEVADMIN_CREATE_EDGE', {
  method: 'POST',
  path: '/devplan/admin/edges',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) state.devplanAdmin.content.edges.push(result);
  },
  failReducer: writeFailed,
});

export const patchEdge = appManager.createApi<
  { id: number; plan: string; kind?: DevEdgeKind; required_rank?: number },
  DevEdgeAdmin,
  AppState
>('DEVADMIN_PATCH_EDGE', {
  method: 'PATCH',
  path: '/devplan/admin/edges/:id',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      replaceExisting(state.devplanAdmin.content.edges, result);
    }
  },
  failReducer: writeFailed,
});

export const deleteEdge = appManager.createApi<
  { id: number; plan: string },
  null,
  AppState
>('DEVADMIN_DELETE_EDGE', {
  method: 'DELETE',
  path: '/devplan/admin/edges/:id',
  startReducer: startSaving,
  successReducer(state, _result, params) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      removeById(state.devplanAdmin.content.edges, params.id);
    }
  },
  failReducer: writeFailed,
});

/* ======================= node ↔ game =================================== */

export const createNodeGame = appManager.createApi<
  { plan: string; node: number; game_asset: number; role: DevGameRole },
  DevNodeGameAdmin,
  AppState
>('DEVADMIN_CREATE_NODE_GAME', {
  method: 'POST',
  path: '/devplan/admin/node-games',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      state.devplanAdmin.content.node_games.push(result);
    }
  },
  failReducer: writeFailed,
});

export const deleteNodeGame = appManager.createApi<
  { id: number; plan: string },
  null,
  AppState
>('DEVADMIN_DELETE_NODE_GAME', {
  method: 'DELETE',
  path: '/devplan/admin/node-games/:id',
  startReducer: startSaving,
  successReducer(state, _result, params) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      removeById(state.devplanAdmin.content.node_games, params.id);
    }
  },
  failReducer: writeFailed,
});

/* ======================= remediation =================================== */

export const patchRemediation = appManager.createApi<
  {
    id: number;
    plan: string;
    gate_name?: string;
    target_game?: number | null;
    target_node?: number | null;
  },
  RemediationRouteAdmin,
  AppState
>('DEVADMIN_PATCH_REMEDIATION', {
  method: 'PATCH',
  path: '/devplan/admin/remediation/:id',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      replaceExisting(state.devplanAdmin.content.remediation, result);
    }
  },
  failReducer: writeFailed,
});

export const deleteRemediation = appManager.createApi<
  { id: number; plan: string },
  null,
  AppState
>('DEVADMIN_DELETE_REMEDIATION', {
  method: 'DELETE',
  path: '/devplan/admin/remediation/:id',
  startReducer: startSaving,
  successReducer(state, _result, params) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      removeById(state.devplanAdmin.content.remediation, params.id);
    }
  },
  failReducer: writeFailed,
});

/* ======================= track / cluster =============================== */

export const patchTrack = appManager.createApi<
  { id: number; plan: string; name?: string; blurb?: string; color?: string; order?: number },
  DevTrackAdmin,
  AppState
>('DEVADMIN_PATCH_TRACK', {
  method: 'PATCH',
  path: '/devplan/admin/tracks/:id',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      replaceExisting(state.devplanAdmin.content.tracks, result);
    }
  },
  failReducer: writeFailed,
});

export const patchCluster = appManager.createApi<
  { id: number; plan: string; name?: string; order?: number },
  DevClusterAdmin,
  AppState
>('DEVADMIN_PATCH_CLUSTER', {
  method: 'PATCH',
  path: '/devplan/admin/clusters/:id',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.content) {
      replaceExisting(state.devplanAdmin.content.clusters, result);
    }
  },
  failReducer: writeFailed,
});

/* ======================= recommendation administration ==================
   Advisory review preserves progression. Only explicit demo BOOST operations
   reset progression; the UI isolates them and requires confirmation. */

// GET /devplan/admin/recommendations/targets — trainees who can see the Development Plan
// tab, each with the sessions they played (the picker's option source).
export const fetchRecTargets = appManager.createApi<
  Record<string, never>,
  DevRecommendationTarget[],
  AppState
>('DEVADMIN_FETCH_REC_TARGETS', {
  method: 'GET',
  path: '/devplan/admin/recommendations/targets',
  successReducer(state, result) {
    state.devplanAdmin.recTargets = Array.isArray(result) ? result : [];
    state.devplanAdmin.recTargetsFetched = true;
  },
  failReducer(state, error) {
    state.devplanAdmin.recTargetsFetched = false;
    state.devplanAdmin.error = stringifyError(error);
  },
});

// GET /devplan/admin/recommendations?plan=<code> — existing rows for the open tree.
export const fetchRecommendations = appManager.createApi<
  { plan: string },
  DevRecommendationAdmin[],
  AppState
>('DEVADMIN_FETCH_RECOMMENDATIONS', {
  method: 'GET',
  path: '/devplan/admin/recommendations',
  successReducer(state, result, params) {
    state.devplanAdmin.recommendations = Array.isArray(result) ? result : [];
    state.devplanAdmin.recommendationsCode = params?.plan;
    state.devplanAdmin.error = undefined;
  },
  failReducer(state, error) {
    state.devplanAdmin.error = stringifyError(error);
  },
});

// POST /devplan/admin/recommendations/generate — create-or-replace + enable (the Re-roll
// button too). Resets the trainee's progress on that tree, then writes the boost.
export const generateRecommendation = appManager.createApi<
  { plan_code: string; user_id: number; session_id: number; k: number },
  DevRecommendationAdmin,
  AppState
>('DEVADMIN_GENERATE_RECOMMENDATION', {
  method: 'POST',
  path: '/devplan/admin/recommendations/generate',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.recommendationsCode !== result.plan_code) return;
    replaceById(state.devplanAdmin.recommendations, result);
    // Mirror server visibility exclusivity within the same (user, plan, mode).
    state.devplanAdmin.recommendations.forEach(r => {
      if (
        r.id !== result.id &&
        r.user === result.user &&
        r.plan === result.plan &&
        r.mode === result.mode
      )
        r.enabled = false;
    });
    if (result.mode === 'boost') state.devplanAdmin.lastRecGranted = result.granted_count;
  },
  failReducer: writeFailed,
});

// ADVISORY visibility changes preserve progression; BOOST toggles retain demo reset semantics.
export const setRecommendationEnabled = appManager.createApi<
  { id: number; enabled: boolean; expected_revision?: string },
  DevRecommendationAdmin,
  AppState
>('DEVADMIN_SET_RECOMMENDATION_ENABLED', {
  method: 'POST',
  path: '/devplan/admin/recommendations/:id/set_enabled',
  startReducer: startSaving,
  successReducer(state, result) {
    endSaving(state);
    if (state.devplanAdmin.recommendationsCode !== result.plan_code) return;
    replaceById(state.devplanAdmin.recommendations, result);
    if (result.enabled) {
      state.devplanAdmin.recommendations.forEach(r => {
        if (
          r.id !== result.id &&
          r.user === result.user &&
          r.plan === result.plan &&
          r.mode === result.mode
        )
          r.enabled = false;
      });
    }
    if (result.mode === 'boost') state.devplanAdmin.lastRecGranted = result.granted_count;
  },
  failReducer: writeFailed,
});

// DELETE removes advice only; an enabled BOOST also resets demo progression.
export const deleteRecommendation = appManager.createApi<
  { id: number },
  unknown,
  AppState
>('DEVADMIN_DELETE_RECOMMENDATION', {
  method: 'DELETE',
  path: '/devplan/admin/recommendations/:id',
  startReducer: startSaving,
  successReducer(state, _result, params) {
    endSaving(state);
    if (params) removeById(state.devplanAdmin.recommendations, params.id);
  },
  failReducer: writeFailed,
});
